import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { USER } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError } from "../helpers";
import { tryConfirmLogin } from "./authReducer";

export interface ICompanyBankAccount {
  _id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
}

class RegCompanyBankAccountErrs {
  form: string | null = null;
  bankName: string | null = null;
  accountName: string | null = null;
  accountNumber: string | null = null;
}

export interface ICompanyBankAccountsState {
  registeringCompanyBankAccount: boolean;
  registeredCompanyBankAccount: boolean;
  regCompanyBankAccountErrs: RegCompanyBankAccountErrs;
  fetchingCompanyBankAccounts: boolean;
  companyBankAccounts: ICompanyBankAccount[];
  fetchCompanyBankAccountsErr: string | null;
  updatingCompanyBankAccount: boolean;
  updatedCompanyBankAccount: boolean;
  updateCompanyBankAccountErrs: RegCompanyBankAccountErrs;
}

const initialState = {
  registeringCompanyBankAccount: false,
  registeredCompanyBankAccount: false,
  regCompanyBankAccountErrs: new RegCompanyBankAccountErrs(),
  fetchingCompanyBankAccounts: false,
  companyBankAccounts: [],
  fetchCompanyBankAccountsErr: null,
  updatingCompanyBankAccount: false,
  updatedCompanyBankAccount: false,
  updateCompanyBankAccountErrs: new RegCompanyBankAccountErrs(),
} as ICompanyBankAccountsState;

const companyBankAccountsSlice = createSlice({
  name: "companyBankAccounts",
  initialState,
  reducers: {
    clearRegCompanyBankAccountFieldErr: (
      state: ICompanyBankAccountsState,
      action: PayloadAction<string>
    ) => {
      state.regCompanyBankAccountErrs[
        action.payload as keyof RegCompanyBankAccountErrs
      ] = null;
    },
    clearRegCompanyBankAccountErrs: (state: ICompanyBankAccountsState) => {
      state.regCompanyBankAccountErrs = new RegCompanyBankAccountErrs();
    },
    tryResetRegCompanyBankAccountFormErr: (
      state: ICompanyBankAccountsState
    ) => {
      let shouldReset = true;

      for (let key in state.regCompanyBankAccountErrs) {
        if (key === "form") {
          continue;
        }

        if (
          state.regCompanyBankAccountErrs[
            key as keyof RegCompanyBankAccountErrs
          ]
        ) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.regCompanyBankAccountErrs.form = null;
      }
    },
    resetRegisteredCompanyBankAccount: (state: ICompanyBankAccountsState) => {
      state.registeredCompanyBankAccount = false;
    },
    regCompanyBankAccountStart: (state: ICompanyBankAccountsState) => {
      state.regCompanyBankAccountErrs = new RegCompanyBankAccountErrs();
      state.registeredCompanyBankAccount = false;
      state.registeringCompanyBankAccount = true;
    },
    regCompanyBankAccountPass: (state: ICompanyBankAccountsState) => {
      state.registeringCompanyBankAccount = false;
      state.registeredCompanyBankAccount = true;
    },
    regCompanyBankAccountFail: (
      state: ICompanyBankAccountsState,
      action: PayloadAction<IFieldError[]>
    ) => {
      state.registeringCompanyBankAccount = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.regCompanyBankAccountErrs) {
          if (err.field === key) {
            state.regCompanyBankAccountErrs[
              key as keyof RegCompanyBankAccountErrs
            ] = err.msg;
            break;
          }
        }
      }

      if (!state.regCompanyBankAccountErrs.form) {
        state.regCompanyBankAccountErrs.form = "Form incorrectly filled.";
      }
    },
    clearFetchCompanyBankAccountsErr: (state: ICompanyBankAccountsState) => {
      state.fetchCompanyBankAccountsErr = null;
    },
    fetchCompanyBankAccountsStart: (state: ICompanyBankAccountsState) => {
      state.fetchCompanyBankAccountsErr = null;
      state.fetchingCompanyBankAccounts = true;
    },
    fetchCompanyBankAccountsPass: (
      state: ICompanyBankAccountsState,
      action: PayloadAction<ICompanyBankAccount[]>
    ) => {
      state.fetchingCompanyBankAccounts = false;

      state.companyBankAccounts = action.payload;
    },
    fetchCompanyBankAccountsFail: (
      state: ICompanyBankAccountsState,
      action: PayloadAction<string>
    ) => {
      state.fetchingCompanyBankAccounts = false;
      state.fetchCompanyBankAccountsErr = action.payload;
    },
    clearUpdateCompanyBankAccountFieldErr: (
      state: ICompanyBankAccountsState,
      action: PayloadAction<string>
    ) => {
      state.updateCompanyBankAccountErrs[
        action.payload as keyof RegCompanyBankAccountErrs
      ] = null;
    },
    clearUpdateCompanyBankAccountErrs: (state: ICompanyBankAccountsState) => {
      state.updateCompanyBankAccountErrs = new RegCompanyBankAccountErrs();
    },
    tryResetUpdateCompanyBankAccountFormErr: (
      state: ICompanyBankAccountsState
    ) => {
      let shouldReset = true;

      for (let key in state.updateCompanyBankAccountErrs) {
        if (key === "form") {
          continue;
        }

        if (
          state.updateCompanyBankAccountErrs[
            key as keyof RegCompanyBankAccountErrs
          ]
        ) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.updateCompanyBankAccountErrs.form = null;
      }
    },
    resetUpdatedCompanyBankAccount: (state: ICompanyBankAccountsState) => {
      state.updatedCompanyBankAccount = false;
    },
    updateCompanyBankAccountStart: (state: ICompanyBankAccountsState) => {
      state.updatedCompanyBankAccount = false;
      state.updateCompanyBankAccountErrs = new RegCompanyBankAccountErrs();
      state.updatingCompanyBankAccount = true;
    },
    updateCompanyBankAccountPass: (
      state: ICompanyBankAccountsState,
      action: PayloadAction<ICompanyBankAccount>
    ) => {
      state.updatingCompanyBankAccount = false;
      state.updatedCompanyBankAccount = true;
    },
    updateCompanyBankAccountFail: (
      state: ICompanyBankAccountsState,
      action: PayloadAction<IFieldError[]>
    ) => {
      state.updatingCompanyBankAccount = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.updateCompanyBankAccountErrs) {
          if (err.field === key) {
            state.updateCompanyBankAccountErrs[
              key as keyof RegCompanyBankAccountErrs
            ] = err.msg;
            break;
          }
        }
      }

      if (!state.updateCompanyBankAccountErrs.form) {
        state.updateCompanyBankAccountErrs.form = "Form incorrectly filled.";
      }
    },
  },
});

export const {
  clearRegCompanyBankAccountErrs,
  clearRegCompanyBankAccountFieldErr,
  tryResetRegCompanyBankAccountFormErr,
  resetRegisteredCompanyBankAccount,
  regCompanyBankAccountStart,
  regCompanyBankAccountPass,
  regCompanyBankAccountFail,
  clearFetchCompanyBankAccountsErr,
  fetchCompanyBankAccountsStart,
  fetchCompanyBankAccountsPass,
  fetchCompanyBankAccountsFail,
  clearUpdateCompanyBankAccountErrs,
  clearUpdateCompanyBankAccountFieldErr,
  tryResetUpdateCompanyBankAccountFormErr,
  resetUpdatedCompanyBankAccount,
  updateCompanyBankAccountStart,
  updateCompanyBankAccountPass,
  updateCompanyBankAccountFail,
} = companyBankAccountsSlice.actions;

interface ITryRegCompanyBankAccountArg {
  data: {
    bankName: string;
    accountName: string;
    accountNumber: string;
  };
  authToken: string;
}

export const tryRegCompanyBankAccount = createAsyncThunk(
  "companyBankAccounts/tryRegCompanyBankAccount",
  (arg: ITryRegCompanyBankAccountArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/company/bank-account`;

    const { data, authToken } = arg;

    dispatch(regCompanyBankAccountStart());

    axios
      .post(endpoint, data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(regCompanyBankAccountPass());
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(regCompanyBankAccountFail(fieldErrors));
      });
  }
);

interface ITryFetchCompanyBankAccountsArg {
  authToken: string;
}

export const tryFetchCompanyBankAccounts = createAsyncThunk(
  "companyBankAccounts/tryFetchCompanyBankAccounts",
  (arg: ITryFetchCompanyBankAccountsArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/company/bank-accounts`;

    const { authToken } = arg;

    dispatch(fetchCompanyBankAccountsStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(fetchCompanyBankAccountsPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }
        let error = "";

        if (data?.err && typeof data?.err === typeof new String("")) {
          error = data.err;
        } else if (data?.errors) {
          error = getFieldErrors(data.errors)[0].msg;
        } else {
          error = "Check your network connection.";
        }

        dispatch(fetchCompanyBankAccountsFail(error));
      });
  }
);

interface ITryUpdateCompanyBankAccountArg {
  id: string;
  data: {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
  };
  authToken: string;
}

export const tryUpdateCompanyBankAccount = createAsyncThunk(
  "companyBankAccounts/tryUpdateCompanyBankAccount",
  (arg: ITryUpdateCompanyBankAccountArg, { dispatch }) => {
    const { id, data, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/company/bank-accounts/${id}`;

    dispatch(updateCompanyBankAccountStart());

    axios
      .put(endpoint, data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(updateCompanyBankAccountPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(updateCompanyBankAccountFail(fieldErrors));
      });
  }
);

export default companyBankAccountsSlice.reducer;
